import { unref } from "vue";
import { auth } from "./auth.js";

export async function get(url, addBaseUrl = true) {
    return doRequest('GET', (addBaseUrl ? import.meta.env.VITE_API_BASE_URL : '') + url)
}

export function put(url, data = null) {
    return doRequest("PUT", import.meta.env.VITE_API_BASE_URL + url, data)
}

export function patch(url, data = null) {
    return doRequest("PATCH", import.meta.env.VITE_API_BASE_URL + url, data)
}

export function post(url, data = null, headers = {}) {
    return doRequest("POST", import.meta.env.VITE_API_BASE_URL + url, data, headers)
}

export function deleteRequest(url, data = null, headers = {}) {
    return doRequest("DELETE", import.meta.env.VITE_API_BASE_URL + url, data, headers)
}

async function doRequest(method, url, data = null, headers = {}) {
    const options = {
        method: method,
        cache: "no-cache",
        headers,
        redirect: "follow",
        referrerPolicy: "no-referrer",
    }

    if (data !== null) {
        options.body = JSON.stringify(data)
        if (typeof options.headers['Content-Type'] === 'undefined') {
            options.headers['Content-Type'] = 'application/json'
        }
    }

    const token = await auth.getAccessTokenSilently()
    if (token) {
        options.headers['Authorization'] = 'Bearer ' + token
    }
    const response = await fetch(url, options);

    return response.json();
}

export async function apiGetAll(url) {
    let items = []
    let addBaseUrl = true
    while (url !== '') {
        const apiResponse = await get(url, addBaseUrl)
        if (apiResponse.items) {
            items = items.concat(apiResponse.items)
        }
        url = ''
        if (apiResponse.hasMore) {
            for (let i = 0; i < apiResponse.links?.length; i++) {
                if (apiResponse.links[i]?.rel === 'next') {
                    url = apiResponse.links[i]?.href?.replace(/^http:\/\//, 'https://')
                    addBaseUrl = false
                }
            }
        }
    }
    return items
}

export async function doBlockUser(selectedAccount, uid) {
    return await post('/v1/access-management/platform-users/block', {}, {
        platform_accounts_id: unref(selectedAccount),
        platform_users_id: unref(uid)
    })
}
export async function doUnblockUser(selectedAccount, uid) {
    return await post('/v1/access-management/platform-users/unblock', {}, {
        platform_accounts_id: unref(selectedAccount),
        platform_users_id: unref(uid)
    })
}
export async function addUserRoles(selectedAccount, uid, roles) {
    const responses = []
    for (let i = 0; i < unref(roles).length; i++) {
        const response = await post('/v1/access-management/role-assignments', {}, {
            platform_accounts_id: unref(selectedAccount),
            platform_users_id: unref(uid),
            roles_id: unref(roles)[i]
        })
        responses.push(response)
    }
    return responses
}
export async function removeUserRoleAssignments(selectedAccount, uid, roleAssignments) {
    const responses = []
    for (let i = 0; i < unref(roleAssignments).length; i++) {
        const response = await deleteRequest('/v1/access-management/role-assignments/' + roleAssignments[i].assignmentId, {}, {
            platform_accounts_id: unref(selectedAccount),
            platform_users_id: unref(uid),
            roles_id: unref(roleAssignments)[i].roleId
        })
        responses.push(response)
    }
    return responses
}
export async function postUser(selectedAccount, email, firstName, lastName, roles) {
    const userResponse = await post('/v1/access-management/platform-users', {}, {
        platform_accounts_id: unref(selectedAccount),
        auth0_user_id: unref(email),
        email: unref(email),
        first_name: unref(firstName),
        last_name: unref(lastName)
    })

    const user = userResponse?.items?.[0]

    if (!user) {
        return false
    }

    const roleResponses = await addUserRoles(selectedAccount, user.id, roles)
    return {
        user,
        roleResponses
    }
}
export async function inviteUser(selectedAccount, email, firstName, lastName, whitelabel = {}) {
    const token = await auth.getAccessTokenSilently()

    return await fetch('/api/InviteUser', {
        method: 'POST',
        cache: "no-cache",
        headers: {
            'ords-authorization': 'Bearer ' + token
        },
        redirect: "follow",
        referrerPolicy: "no-referrer",
        body: JSON.stringify({
            platformAccount: unref(selectedAccount),
            email: unref(email),
            displayName: unref(firstName) + ' ' + unref(lastName),
            wl: whitelabel
        })
    })
}

export async function getUsers(selectedAccount) {
    return await apiGetAll('/v1/access-management/platform-users?limit=100&q={"cephisso_platform_accounts_id":{"$eq":' + unref(selectedAccount) + '}}')
}
export async function getRoleAssignments(selectedAccount) {
    return await apiGetAll('/v1/access-management/role-assignments?limit=100&q={"cephisso_platform_accounts_id":{"$eq":' + unref(selectedAccount) + '}}')
}
export async function getRoles(selectedAccount) {
    return await apiGetAll('/v1/access-management/roles')
}
export async function getUserGroupMemberships(selectedAccount) {
    return await apiGetAll('/v1/cephisso-platform-user-group-memberships?limit=100&q={"cephisso_platform_accounts_id":{"$eq":' + unref(selectedAccount) + '}}')
}
export async function getUserGroups(selectedAccount) {
    return await apiGetAll('/v1/cephisso-platform-user-groups?limit=100&q={"cephisso_platform_accounts_id":{"$eq":' + unref(selectedAccount) + '}}')
}
export async function getKnowledgeSourceTypes(selectedAccount) {
    return await apiGetAll('/crbn-tech/knowledge-source-types?limit=100&q={"$or":[{"cephisso_platform_accounts_id":{"$eq":' + unref(selectedAccount) + '}}, {"cephisso_platform_accounts_id":{"$eq":0}}]}')
}
export async function getKnowledgeSources(selectedAccount) {
    return await apiGetAll('/crbn-tech/ai-knowledge-source?limit=100&q={"cephisso_platform_accounts_id":{"$eq":' + unref(selectedAccount) + '}}')
}
export async function getModels(selectedAccount) {
    return await apiGetAll('/crbn-tech/llm-models?limit=100&q={"$or":[{"cephisso_platform_accounts_id":{"$eq":' + unref(selectedAccount) + '}}, {"cephisso_platform_accounts_id":{"$eq":0}}]}')
}
export async function getModelDeployments(selectedAccount) {
    return await apiGetAll('/crbn-tech/llm-model-deployments?limit=100&q={"$or":[{"cephisso_platform_accounts_id":{"$eq":' + unref(selectedAccount) + '}}, {"cephisso_platform_accounts_id":{"$eq":0}}]}')
}
export async function getModelDeploymentCredentials(selectedAccount) {
    return await apiGetAll('/crbn-tech/ai-model-deployment-credentials?limit=100&q={"$or":[{"cephisso_platform_accounts_id":{"$eq":' + unref(selectedAccount) + '}}, {"cephisso_platform_accounts_id":{"$eq":0}}]}')
}
export async function getLlmQuotas(selectedAccount) {
    return await apiGetAll('/crbn-tech/ai-credit-quotas?limit=100&q={"cephisso_platform_accounts_id":{"$eq":' + unref(selectedAccount) + '}}')
}
export async function getLlmQuotaAssignments(selectedAccount) {
    return await apiGetAll('/crbn-tech/ai-credit-quota-assignments?limit=100&q={"cephisso_platform_accounts_id":{"$eq":' + unref(selectedAccount) + '}}')
}
export async function getSkills(selectedAccount) {
    return await apiGetAll('/crbn-tech/ai-skills?limit=100&spheres_id=' + unref(selectedAccount))
}
export async function getSkillStepTypes(selectedAccount) {
    return await apiGetAll('/crbn-tech/ai-skill-step-types?limit=100&&q={"$or":[{"cephisso_platform_accounts_id":{"$eq":' + unref(selectedAccount) + '}}, {"cephisso_platform_accounts_id":{"$eq":0}}]}')
}
export async function getSkillSteps(selectedAccount) {
    return await apiGetAll('/crbn-tech/ai-skill-steps?limit=100&q={"$or":[{"cephisso_platform_accounts_id":{"$eq":' + unref(selectedAccount) + '}}, {"cephisso_platform_accounts_id":{"$eq":0}}]}')
}
export async function getSkillAccessGrants(selectedAccount) {
    return await apiGetAll('/crbn-tech/ai-skill-access-grants?limit=100&q={"$or":[{"cephisso_platform_accounts_id":{"$eq":' + unref(selectedAccount) + '}}, {"cephisso_platform_accounts_id":{"$eq":0}}]}')
}
export async function getLlmModelDeploymentAccess(selectedAccount) {
    return await apiGetAll('/crbn-tech/llm-model-deployment-access?limit=100&q={"cephisso_platform_accounts_id":{"$eq":' + unref(selectedAccount) + '}}')
}
export async function getAccounts() {
    return await apiGetAll('/v1/cephisso-platform-accounts?limit=500')
}
export async function getSkillGroupings(selectedAccount) {
    return await apiGetAll('/crbn-tech/ai-skill-groupings?limit=100&q={"cephisso_platform_accounts_id":{"$eq":' + unref(selectedAccount) + '}}')
}
export async function getSkillGroupingAssignments(selectedAccount) {
    return await apiGetAll('/crbn-tech/ai-skill-grouping-assignments?limit=100&q={"cephisso_platform_accounts_id":{"$eq":' + unref(selectedAccount) + '}}')
}
export async function getAiBots(selectedAccount) {
    return await apiGetAll('/crbn-tech/ai-bots?limit=100&q={"cephisso_platform_accounts_id":{"$eq":' + unref(selectedAccount) + '}}')
}
export async function getAiBotSkillAssignments(selectedAccount) {
    return await apiGetAll('/crbn-tech/ai-bots-skill-assignment?limit=100&q={"cephisso_platform_accounts_id":{"$eq":' + unref(selectedAccount) + '}}')
}
export async function getAiBotAccessGrants(selectedAccount) {
    return await apiGetAll('/crbn-tech/ai-bot-access-grants?limit=100&q={"$or":[{"cephisso_platform_accounts_id":{"$eq":' + unref(selectedAccount) + '}}, {"cephisso_platform_accounts_id":{"$eq":0}}]}')
}